/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\ServerInfoWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useEffect, useContext } from 'react';
import { Terminal, AppWindow } from 'lucide-react';
import { useBranding } from '../../../hooks/useBranding';
import { WidgetContext } from '../components/WidgetWrapper';

interface ServerInfo {
    platform: string;
    distro?: string;
    release: string; // Kernel
    // We might have extended info from API now:
    versions?: {
        node: string;
        app: string;
    };
    services?: Record<string, { status: string; version: string }>;
}

interface ServerInfoWidgetProps {
    isEditMode?: boolean;
}

export function ServerInfoWidget({ isEditMode }: ServerInfoWidgetProps) {
    const { brandingData } = useBranding();
    const [info, setInfo] = useState<ServerInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const widgetPrefs = useContext(WidgetContext);
    const borderColor = widgetPrefs?.borderColor || 'rgba(255,255,255,0.1)';

    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const protocol = window.location.protocol;
                const host = window.location.host;
                const response = await fetch(`${protocol}//${host}/monitoring-api/api/system/stats`);

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.data) {
                        setInfo({
                            platform: data.data.platform,
                            distro: data.data.distro,
                            release: data.data.release,
                            versions: data.data.versions,
                            services: data.data.services
                        });
                        setError(null);
                    }
                }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                setError('Failed to load system info');
            } finally {
                setLoading(false);
            }
        };

        fetchInfo();
        const interval = setInterval(fetchInfo, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error || !info) {
        return (
            <div className="h-full flex items-center justify-center text-muted-foreground">
                <span className="text-sm">{error || 'No data available'}</span>
            </div>
        );
    }

    // Prepare fields
    // Use dynamic distro from API or fallback to platform
    const distroName = info.distro || (info.platform === 'linux' ? 'Linux' : info.platform);
    const kernel = info.release;

    // FreeSWITCH Version
    const fsVersion = info.services?.freeswitch?.version || 'N/A';

    // App Version
    const appVersion = info.versions?.app || 'Unknown';

    // Runtime Version
    const nodeVersion = info.versions?.node || 'N/A';

    // Get branded name
    const appLabel = brandingData?.brandingConfig?.appName || 'Ring2All';

    const items = [
        {
            label: 'Distro',
            value: distroName,
            iconClass: "text-red-500" // Debian swatch color
        },
        {
            label: 'Kernel',
            value: kernel
        },
        {
            label: 'PBX Engine',
            value: fsVersion
        },
        {
            label: appLabel,
            value: appVersion
        },
        {
            label: 'Node Version',
            value: nodeVersion
        }
    ];

    return (
        <div className="h-full pr-1 overflow-hidden flex flex-col">
            <div className="flex-1 flex flex-col justify-between pt-1 pb-1">
                {items.map((item, idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:justify-between sm:items-center pb-1 last:border-0 last:pb-0"
                         style={{ borderBottom: idx === items.length - 1 ? 'none' : `1px solid ${borderColor}` }}>
                        <span className="text-xs text-muted-foreground font-medium">{item.label}</span>
                        <div className="flex items-center gap-2">
                            {/* Simple logo indicator for Distro if needed */}
                            {item.label === 'Distro' && (
                                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-red-600 fill-current" xmlns="http://www.w3.org/2000/svg"><path d="M12.984 21.656C16.852 20.47 18.25 17.584 17.15 13.91c-1.123-3.08-3.033-3.664-5.06-2.583-1.605 1.002-2.128 3.167-1.168 5.617 0.99 2.528 3.42 2.628 5.433 0.224 0.368-0.567 1.003-0.59 1.15 0.117 0.17 0.816-0.342 1.517-1.042 1.933-2.6 1.192-5.758 0.617-7.05-1.283-1.467-2.617-0.85-6.192 1.375-7.975 2.2-1.992 5.092-2.325 8.65-0.975 3.65 1.383 10.4 4.5 12.067 8.017 1.258 2.65-0.125 5.567-3.092 6.783-2.967 1.217-6.283-0.083-7.408-2.908-1.508-3.792 0.308-7.233 4.058-7.692 3.8-0.467 6.95 2.217 7.917 6.133z" /></svg>
                            )}
                            <span className="text-xs font-medium text-foreground text-right truncate max-w-[12rem]">{item.value}</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
